/* istanbul ignore file */
// Next
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import Head from 'next/head'

import { IntercomProvider } from 'react-use-intercom'

import { SessionProvider } from 'next-auth/react'

import { ClientPage } from 'components/ClientPage'
import { Layout } from 'layout'
import 'lib/analytics'
import { NotificationProvider } from 'lib/context/NotificationContext'
import Script from 'next/script'
import 'styles/font-faces.css'

import 'styles/swile-icons.css'
import 'styles/tailwind-globals.css'

import { AnalyticsMetadata } from 'components/AnalyticsMetadata/AnalyticsMetadata'
import { GoogleAnalytics, GoogleTagManager } from '@next/third-parties/google'
import ErrorBoundary from 'components/ErrorBoundary'
import { ThemeProvider } from '../providers/ThemeProvider'
import { I18nProvider } from '../providers/I18nProvider'
import { DEFAULT_LOCALE } from 'i18n'

type GuestPage<P = object> = NextPage<P> & {
  template?: 'guest' | 'zendesk'
}

type Props = AppProps & {
  Component: GuestPage
}

const INTERCOM_APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID

const zendeskAppUrl =
  'https://static.zdassets.com/ekr/snippet.js?key=' +
  process.env.NEXT_PUBLIC_APP_ZENDESK_KEY

function App({ Component, pageProps: { session, ...pageProps } }: Props) {
  return (
    <I18nProvider locale={DEFAULT_LOCALE}>
      <ThemeProvider theme="light">
        <ErrorBoundary>
          <SessionProvider session={session}>
            <Head>
              <meta
                name="viewport"
                content="width=device-width,initial-scale=1"
              />
              <meta name="author" content="Swile" />
              <meta property="og:locale" content="pt_BR" />
              <meta name="robots" content="noindex, nofollow" />
              <meta name="theme-color" content="#f7f7f7" />

              <title>Swile Corporates</title>
              <link rel="manifest" href="/manifest.json" />

              <link
                rel="icon"
                type="image/png"
                href="/assets/favicon/favicon-light-32x32.png"
                sizes="32x32"
                media="(prefers-color-scheme: light)"
              />
              <link
                rel="icon"
                type="image/png"
                href="/assets/favicon/favicon-light-128x128.png"
                sizes="128x128"
                media="(prefers-color-scheme: light)"
              />
              <link
                rel="apple-touch-icon"
                href="/assets/favicon/favicon-light-180x180.png"
                sizes="180x180"
                media="(prefers-color-scheme: light)"
              />
              <link
                rel="apple-touch-icon"
                href="/assets/favicon/favicon-light-192x192.png"
                sizes="192x192"
                media="(prefers-color-scheme: light)"
              />
              <link
                rel="icon"
                type="image/png"
                href="/assets/favicon/favicon-32x32.png"
                sizes="32x32"
                media="(prefers-color-scheme: dark)"
              />
              <link
                rel="icon"
                type="image/png"
                href="/assets/favicon/favicon-128x128.png"
                sizes="128x128"
                media="(prefers-color-scheme: dark)"
              />
              <link
                rel="apple-touch-icon"
                href="/assets/favicon/favicon-180x180.png"
                sizes="180x180"
                media="(prefers-color-scheme: dark)"
              />
              <link
                rel="apple-touch-icon"
                href="/assets/favicon/favicon-192x192.png"
                sizes="192x192"
                media="(prefers-color-scheme: dark)"
              />
            </Head>
            <GoogleAnalytics gaId="G-69JDFW1S67" />
            <GoogleTagManager gtmId="GTM-MK4Q2B2L" />
            <NotificationProvider>
              {Component.template === 'guest' ? (
                <Component {...pageProps} />
              ) : Component.template === 'zendesk' ? (
                <>
                  <Component {...pageProps} />
                  <Script
                    src={zendeskAppUrl}
                    strategy="lazyOnload"
                    id="ze-snippet"
                  />
                </>
              ) : (
                <IntercomProvider
                  appId={INTERCOM_APP_ID ? INTERCOM_APP_ID : ''}
                >
                  <Layout>
                    <AnalyticsMetadata />
                    <Component {...pageProps} />
                    <Script
                      src={zendeskAppUrl}
                      strategy="lazyOnload"
                      id="ze-snippet"
                    />
                  </Layout>
                </IntercomProvider>
              )}
            </NotificationProvider>
          </SessionProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </I18nProvider>
  )
}

function ClientApp(props: Props) {
  return (
    <ClientPage>
      <App {...props} />
    </ClientPage>
  )
}

export default ClientApp
